import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { DraggableComponent } from '../draggable/draggable.component';
import { DraggableComponentEvent } from '../shared/events/drag-drop-component.event';
import {
  DRAG_DROP_RELEASED,
  DRAG_DROP_RELEASED_OUTSIDE_EVENT,
  DRAGGING_INSIDE_DROPZONE,
  DRAGGING_OUTSIDE_DROPZONE,
} from '../shared/events/drag-drop.events';
import { BoundsService } from '../shared/services/bounds.service';
import { DragDropPositionService } from '../shared/services/drag-drop-position.service';
import { DragDropService } from '../shared/services/drag-drop.service';
import { DropZoneComponent } from './drop-zone.component';

export class DropZoneService {
  #dragDropService: DragDropService;

  #hasOwnDragDropService = true;

  #draggableComponents: Array<DraggableComponent> = [];

  #draggablesChangedFn?: (d: Array<DraggableComponent>) => void;

  #gutterSize = 0;

  #collapse = true;

  #whiteSpaceHeight = 0;

  #whiteSpaceIndex = -1;

  #threshold = '20%';

  #redrawTimeout?: number;

  #draggingOutside = false;

  #externalDrag = false;

  #contentManagedExternally = false;

  #limit = -1;

  #singleValue = false;

  /** setters / getters */
  setCollapse(collapse: boolean) {
    this.#collapse = collapse;
  }

  setDraggingOutside(draggingOutside: boolean) {
    this.#draggingOutside = draggingOutside;
  }

  setDraggableComponents(draggableComponents: Array<DraggableComponent>) {
    this.#draggableComponents = draggableComponents;
    this.#draggablesChangedFn?.(draggableComponents);
    this.setIndexDraggables(draggableComponents);
  }

  setDragDropService(service: DragDropService) {
    if (this.#dragDropService !== service) {
      this.#dragDropService.unregisterDropZone(this.dropzoneComponent);
      this.#dragDropService = service;
      this.#hasOwnDragDropService = false;
      this.#dragDropService.disableResetDropperOutside();
    }
  }

  registerDraggablesChanged(
    callback: (draggables: Array<DraggableComponent>) => void,
  ) {
    this.#draggablesChangedFn = callback;
    this.#draggablesChangedFn(this.#draggableComponents);
  }

  setDisableOutsideBounceBack(value: boolean) {
    if (value) {
      this.#dragDropService.disableResetDropperOutside();
    } else {
      this.#dragDropService.enableResetDropperOutside();
    }
  }

  setContentManagedExternally(contentManagedExternally: boolean) {
    this.#contentManagedExternally = contentManagedExternally;
  }

  /** life cycle */

  constructor(
    private readonly dropzoneComponent: DropZoneComponent,
    dragDropService: DragDropService = new DragDropService(),
  ) {
    this.#dragDropService = dragDropService;
  }

  initialize(draggableComponents: Array<DraggableComponent>) {
    this.setDraggableComponents(draggableComponents);
    this.setIndexDraggables(draggableComponents);
    this.layoutInvalidated(false);
  }

  /** positioning */

  getRelativePositionIndex(index: number) {
    return (
      DragDropPositionService.calculatePositionByIndex(
        this.#draggableComponents,
        index,
        this.#gutterSize,
        this.#collapse,
        this.#draggingOutside,
        true,
        true,
      ) +
      this.dropzoneComponent.getBoundingClientRect().top -
      this.dropzoneComponent.scrollTop
    );
  }

  getPaddingLeft() {
    const { paddingLeft } = getComputedStyle(this.dropzoneComponent);
    if (!paddingLeft) {
      return 0;
    }

    if (paddingLeft.includes('px')) {
      return parseInt(paddingLeft, 10);
    }

    throw new Error(`can't convert padding left: '${paddingLeft}'.`);
  }

  calculatePosition(
    index: number,
    component: DraggableComponent,
    axis: 'x' | 'y',
    checkCollapse = true,
    includeZeroPosition = true,
  ): number {
    const zeroPosition = DragDropPositionService.calculateZeroPosition(
      this.#draggableComponents,
      index,
      axis === 'x',
    );

    const currentPosition = DragDropPositionService.calculatePositionByIndex(
      this.#draggableComponents,
      component.index,
      this.#gutterSize,
      this.#collapse,
      this.#draggingOutside,
      checkCollapse,
      includeZeroPosition,
      this.#whiteSpaceIndex,
      this.#whiteSpaceHeight,
      axis === 'x',
    );

    return includeZeroPosition
      ? zeroPosition + currentPosition
      : currentPosition;
  }

  getStartPositionDraggable(draggable: DraggableComponent) {
    const rect = this.dropzoneComponent.getBoundingClientRect();

    const htmlIndex = this.#draggableComponents.indexOf(draggable);

    const position = { x: 0, y: 0 };
    position.x = rect.left + draggable.startPosition.x;
    position.y =
      rect.top +
      this.calculatePosition(htmlIndex, draggable, 'y', false, false) -
      this.dropzoneComponent.scrollTop;

    return position;
  }

  layoutInvalidated(animate = true) {
    const isHorizontal = this.dropzoneComponent.horizontal;

    this.#draggableComponents.forEach((component, index) => {
      if (component.dragging) {
        return;
      }

      const position = isHorizontal
        ? { x: this.calculatePosition(index, component, 'x'), y: 0 }
        : { x: 0, y: this.calculatePosition(index, component, 'y') };

      gsap.to(
        component,
        animate ? DragDropService.ANIMATION_TIME_IN_SECONDS - 0.05 : 0,
        position,
      );

      component.updatePosition({ x: position.x, y: position.y });
    });
  }

  animateDraggableToCorrectPosition(
    draggable: DraggableComponent,
    delta: { x: number; y: number },
  ) {
    const rect = this.dropzoneComponent.getBoundingClientRect();

    gsap.to(draggable, 0.2, {
      left: 0,
      x: delta.x + rect.x,
      y:
        delta.y +
        rect.y +
        DragDropPositionService.calculatePositionByIndex(
          this.#draggableComponents,
          draggable.index,
          this.#gutterSize,
          this.#collapse,
          this.#draggingOutside,
          true,
          true,
        ) -
        this.dropzoneComponent.scrollTop,
    });
  }

  getValue() {
    return [...this.#draggableComponents]
      .sort((a, b) => {
        return a.index > b.index ? 1 : -1;
      })
      .reduce((result, component) => {
        if (component.value) {
          return [...result, component.value];
        }

        return result;
      }, [] as Array<unknown>);
  }

  /** on drag */

  onDrag(draggingComponent: DraggableComponent, event: PointerEvent) {
    // Check dragging outside
    const isDraggingOutside =
      this.#collapse &&
      !Draggable.hitTest(
        draggingComponent,
        this.dropzoneComponent,
        this.#threshold,
      );

    if (this.dropzoneComponent.restrictDraggingOutside && isDraggingOutside) {
      return;
    }

    // Set dragging outside but only invalidate if it's changed
    if (this.#draggingOutside !== isDraggingOutside) {
      this.dropzoneComponent.dispatchEvent(
        new DraggableComponentEvent(
          isDraggingOutside
            ? DRAGGING_OUTSIDE_DROPZONE
            : DRAGGING_INSIDE_DROPZONE,
          draggingComponent,
        ),
      );

      // Required to set before invalidation
      this.#draggingOutside = isDraggingOutside;
      this.setDraggingOutside(this.#draggingOutside);
      this.layoutInvalidated();
    } else {
      this.#draggingOutside = isDraggingOutside;
      this.setDraggingOutside(this.#draggingOutside);
    }

    // When dragging outside there shouldn't be a redraw
    if (this.#draggingOutside && this.#redrawTimeout) {
      clearTimeout(this.#redrawTimeout);
    }

    if (this.#limit > this.#draggableComponents.length || this.#limit === -1) {
      // Check and handle collisions
      this.#draggableComponents.forEach((hittestComponent) => {
        if (
          draggingComponent === hittestComponent ||
          hittestComponent.disabled
        ) {
          return;
        }
        const colliding = Draggable.hitTest(
          draggingComponent,
          hittestComponent,
          this.#threshold,
        );

        if (colliding) {
          this.handleCollision(event, draggingComponent, hittestComponent);
        }
      });
    }
  }

  handleCollision(
    event: PointerEvent,
    draggingComponent: DraggableComponent,
    hittestComponent: DraggableComponent,
  ) {
    const positionEvent =
      window.TouchEvent && event instanceof TouchEvent
        ? event.touches[0]
        : event;
    const isHorizontal = this.dropzoneComponent.horizontal;
    const mousePosition = isHorizontal
      ? positionEvent.clientX
      : positionEvent.clientY;
    const isBelow = draggingComponent.index > hittestComponent.index;
    const shouldBeBelow =
      mousePosition >
      BoundsService.getCenterOfRect(
        hittestComponent.getBoundingClientRect(),
        isHorizontal,
      );

    if (this.#externalDrag) {
      const newIndex = hittestComponent.index + (shouldBeBelow ? 1 : 0);
      this.#insertWhiteSpaceInPosition(
        draggingComponent.height,
        hittestComponent.index + (shouldBeBelow ? 1 : 0),
      );
      draggingComponent.setIndex(newIndex);
    } else if (isBelow !== shouldBeBelow) {
      this.#insertDraggableComponentInPosition(
        draggingComponent,
        hittestComponent.index,
      );
    }
  }

  #insertWhiteSpaceInPosition(height: number, targetIndex: number) {
    this.#whiteSpaceHeight = height + this.#gutterSize;
    this.#whiteSpaceIndex = targetIndex;

    this.layoutInvalidated();
  }

  #insertDraggableComponentInPosition(
    insertedComponent: DraggableComponent,
    targetIndex: number,
  ) {
    const components = [...this.#draggableComponents];
    const filteredComponents = components.filter(
      (component) => component !== insertedComponent,
    );

    filteredComponents.sort((a, b) => a.index - b.index);

    const isDisabledAtIndex = (index: number) => {
      return components.some((comp) => comp.disabled && comp.index === index);
    };

    const originalIndex = insertedComponent.index;
    const movingForward = targetIndex > originalIndex;

    filteredComponents.forEach((component) => {
      if (component.disabled) {
        return;
      }

      let newIndex = component.index;

      if (movingForward) {
        if (component.index > originalIndex && component.index <= targetIndex) {
          newIndex -= 1;
        }
      } else if (
        component.index >= targetIndex &&
        component.index < originalIndex
      ) {
        newIndex += 1;
      }

      while (isDisabledAtIndex(newIndex)) {
        newIndex += movingForward ? -1 : 1;
      }

      component.setIndex(newIndex);
    });

    insertedComponent.setIndex(targetIndex);

    this.layoutInvalidated();
  }

  /** on release */

  onRelease(releasingComponent: DraggableComponent, colliding = false) {
    return this.#externalDrag || !colliding
      ? this.onReleaseExternal(releasingComponent, colliding)
      : this.onReleaseInternal(releasingComponent);
  }

  onReleaseExternal(
    releasingComponent: DraggableComponent,
    colliding: boolean,
  ) {
    const anchor = this.#draggableComponents.find(
      (draggable) => draggable.index === this.#whiteSpaceIndex,
    );

    if (this.#contentManagedExternally) {
      if (colliding) {
        this.#broadcastValue(releasingComponent);
      }
      this.#resetExternalDrag();
    } else if (colliding) {
      if (this.#draggableComponents.length >= this.#limit) {
        this.dropzoneComponent.broadCastEvent(
          'limitExceeded',
          releasingComponent,
        );
      }

      setTimeout(
        () => {
          this.#resetExternalDrag();

          if (anchor) {
            anchor?.before(releasingComponent);
          } else {
            this.dropzoneComponent.appendChild(releasingComponent);
          }

          this.dropzoneComponent.redraw(false);
          this.#onReleaseChangesMade();
        },
        DragDropService.ANIMATION_TIME_IN_SECONDS * 1000 +
          DragDropService.DELAY_AFTER_ANIMATION,
      );
    }
  }

  setValue(value: DraggableComponent) {
    if (!value) {
      return;
    }

    value.setSelected(false);

    this.#resetExternalDrag();
    this.dropzoneComponent.innerHTML = '';
    this.dropzoneComponent.appendChild(value);
    this.dropzoneComponent.redraw(false);

    this.#onReleaseChangesMade();
  }

  #resetExternalDrag() {
    this.#whiteSpaceHeight = 0;
    this.#whiteSpaceIndex = -1;
    this.#externalDrag = false;
  }

  #broadcastValue(releasingComponent: DraggableComponent) {
    let value = this.getValue();

    if (value.length) {
      if (
        value.length === this.#whiteSpaceIndex ||
        this.#whiteSpaceIndex === -1
      ) {
        value.push(releasingComponent.value);
      } else if (this.#whiteSpaceIndex === 0) {
        value.unshift(releasingComponent.value);
      } else {
        value.splice(this.#whiteSpaceIndex, 0, releasingComponent.value);
      }
    } else {
      value = [releasingComponent.value];
    }

    this.#redrawTimeout = setTimeout(
      () => {
        this.dropzoneComponent.broadCastEvent('input', value);
        this.dropzoneComponent.broadCastEvent('change', value);
      },
      // hacky but otherwise animations will overlap and do some magic stuff to the positioning.
      DragDropService.ANIMATION_TIME_IN_SECONDS * 1000 + 20,
    ) as unknown as number;
  }

  onReleaseInternal(releasingComponent: DraggableComponent) {
    this.dropzoneComponent.dispatchEvent(
      new DraggableComponentEvent(DRAG_DROP_RELEASED, releasingComponent),
    );

    const isColliding = Draggable.hitTest(
      releasingComponent,
      this.dropzoneComponent,
      this.#threshold,
    );

    if (!isColliding && !releasingComponent.clicked) {
      this.dropzoneComponent.dispatchEvent(
        new DraggableComponentEvent(
          DRAG_DROP_RELEASED_OUTSIDE_EVENT,
          releasingComponent,
        ),
      );
    }

    const changesMade = !this.noChangesMade(releasingComponent);
    if (changesMade) {
      this.#onReleaseChangesMade();
    }

    // Layout invalidated on different thread to give an option to manipulate DOM before animation
    setTimeout(() => {
      this.layoutInvalidated();
    });
  }

  #onReleaseChangesMade() {
    this.#redrawTimeout = setTimeout(
      () => {
        this.dropzoneComponent.redraw();
        this.dropzoneComponent.broadCastEvent('input', this.getValue());
        this.dropzoneComponent.broadCastEvent('change', this.getValue());
      },
      // hacky but otherwise animations will overlap and do some magic stuff to the positioning.
      DragDropService.ANIMATION_TIME_IN_SECONDS * 1000 +
        DragDropService.DELAY_AFTER_ANIMATION,
    ) as unknown as number;
  }

  /** misc */

  noChangesMade(releasingComponent: DraggableComponent): boolean {
    if (
      !Draggable.hitTest(
        releasingComponent,
        this.dropzoneComponent,
        this.#threshold,
      )
    ) {
      return true;
    }

    const htmlIndex = this.#draggableComponents.indexOf(releasingComponent);
    const newYPosition = this.calculatePosition(
      htmlIndex,
      releasingComponent,
      'y',
      false,
    );

    return newYPosition === releasingComponent.startPosition.y;
  }

  externalDropStart() {
    this.#externalDrag = true;
    this.layoutInvalidated();
  }

  setIndexDraggables(draggabledComponents: Array<DraggableComponent>) {
    draggabledComponents.forEach((component, index) => {
      component.setIndex(
        component.dragIndex !== undefined ? component.dragIndex : index,
      );
    });
  }

  useDropper(useDropper: boolean) {
    if (!this.#hasOwnDragDropService) {
      return;
    }

    if (useDropper) {
      this.#dragDropService.enableDropper();
    } else {
      this.#dragDropService.disableDropper();
    }
  }

  containsDraggable(draggable: DraggableComponent) {
    return this.#draggableComponents.includes(draggable);
  }

  externalDropCancel() {
    this.#externalDrag = false;
    this.#whiteSpaceIndex = -1;
    this.#whiteSpaceHeight = 0;
    this.layoutInvalidated();
  }

  setSingleValue(singleValue: boolean) {
    this.#limit = singleValue ? 1 : -1;
    this.#singleValue = singleValue;
  }

  getSingleValue() {
    return this.#singleValue;
  }
}
